import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Sunrise, CalendarDays, ArrowLeft, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Row = ({ icon: Icon, title, description, checked, onToggle, testId }) => (
  <div className="flex items-start gap-4 py-4">
    <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
         style={{ background: 'rgba(56,189,248,0.15)', border: '1px solid rgba(125,211,252,0.35)' }}>
      <Icon className="w-5 h-5 text-sky-300" strokeWidth={1.7} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium text-sm sm:text-base">{title}</p>
      <p className="text-white/60 text-xs sm:text-sm mt-0.5 leading-relaxed">{description}</p>
    </div>
    <button
      type="button"
      onClick={onToggle}
      role="switch"
      aria-checked={checked}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition mt-1 ${checked ? 'bg-sky-500' : 'bg-white/20'}`}
      data-testid={testId}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition mt-0.5 ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </button>
  </div>
);

const EmailPreferencesPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [prefs, setPrefs] = useState({
    bolt_reminder_enabled: false,
    bolt_weekly_summary_enabled: false,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setPrefs({
          bolt_reminder_enabled: !!r.data.bolt_reminder_enabled,
          bolt_weekly_summary_enabled: !!r.data.bolt_weekly_summary_enabled,
        });
      } catch { /* keep defaults */ } finally { setLoading(false); }
    };
    load();
  }, []);

  const patch = async (partial) => {
    if (saving) return;
    setSaving(true);
    const optimistic = { ...prefs, ...partial };
    setPrefs(optimistic);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.patch(`${API}/auth/preferences`, partial, { headers: { Authorization: `Bearer ${token}` } });
      setPrefs({
        bolt_reminder_enabled: !!r.data.bolt_reminder_enabled,
        bolt_weekly_summary_enabled: !!r.data.bolt_weekly_summary_enabled,
      });
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
    } catch {
      // revert
      setPrefs((p) => ({ ...p, ...Object.fromEntries(Object.keys(partial).map(k => [k, !partial[k]])) }));
    } finally { setSaving(false); }
  };

  const disableAll = () => patch({ bolt_reminder_enabled: false, bolt_weekly_summary_enabled: false });

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6" style={{
      background: 'linear-gradient(135deg, #0c1929 0%, #1a365d 50%, #0f2744 100%)',
      fontFamily: 'Figtree, sans-serif',
    }} data-testid="email-preferences-page">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm mb-4" data-testid="email-prefs-back">
          <ArrowLeft size={16} /> Retour
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg"
               style={{ background: 'linear-gradient(135deg, rgba(56,189,248,0.25), rgba(2,132,199,0.4))', border: '1px solid rgba(125,211,252,0.4)' }}>
            <Mail className="w-8 h-8 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl sm:text-4xl text-white font-light" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Préférences <span className="font-medium text-sky-300">email</span>
          </h1>
          <p className="text-white/70 mt-2 text-sm sm:text-base">Choisissez les messages que vous souhaitez recevoir.</p>
        </div>

        <div className="rounded-2xl p-2 sm:p-6"
             style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(18px)', border: '1px solid rgba(255,255,255,0.18)' }}>
          {loading ? (
            <p className="text-white/70 text-sm p-6 text-center">Chargement…</p>
          ) : (
            <div className="divide-y divide-white/10 px-3">
              <Row
                icon={Sunrise}
                title="Rappel BOLT quotidien"
                description="Un email chaque matin à 9h00 pour vous inviter à mesurer votre BOLT à jeun. Uniquement les jours où vous n'avez pas encore mesuré."
                checked={prefs.bolt_reminder_enabled}
                onToggle={() => patch({ bolt_reminder_enabled: !prefs.bolt_reminder_enabled })}
                testId="pref-daily-toggle"
              />
              <Row
                icon={CalendarDays}
                title="Récapitulatif hebdomadaire"
                description="Chaque dimanche à 10h00 : votre moyenne, votre meilleure valeur et votre progression BOLT de la semaine."
                checked={prefs.bolt_weekly_summary_enabled}
                onToggle={() => patch({ bolt_weekly_summary_enabled: !prefs.bolt_weekly_summary_enabled })}
                testId="pref-weekly-toggle"
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-6 gap-3 flex-wrap">
          <button
            onClick={disableAll}
            disabled={saving || (!prefs.bolt_reminder_enabled && !prefs.bolt_weekly_summary_enabled)}
            className="text-xs text-white/60 hover:text-white/90 underline disabled:opacity-40 disabled:no-underline"
            data-testid="pref-disable-all"
          >
            Tout désactiver
          </button>
          {savedAt && (
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-300" data-testid="pref-saved">
              <Check size={14} /> Enregistré
            </span>
          )}
        </div>

        <p className="text-white/40 text-[11px] mt-8 text-center leading-relaxed">
          Les emails d&apos;inscription, d&apos;approbation de compte et de réinitialisation de mot de passe restent toujours envoyés<br className="hidden sm:block" />
          car ils sont indispensables au bon fonctionnement de votre compte.
        </p>
      </div>
    </div>
  );
};

export default EmailPreferencesPage;
